import { useNavigate } from "react-router-dom";
import {
  LayoutList,
  Building2,
  FileSignature,
  Gavel,
  FlagTriangleRight,
  FileText,
  FilePlus2,
  FileEdit,
  Receipt,
  Wallet,
  ChevronRight,
  ChevronsUpDown,
} from "lucide-react";
import { useRecoilValue } from "recoil";
import Swal from "sweetalert2";
import { userState } from "../common/atom/userState";
import { useLogout } from "../common/logout";
import { approvalMenuList } from "../lib/menuList";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "./ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const menuIconMap = {
  "/loa/all": LayoutList,
  "/loa/vendorsEvaluation": Building2,
  "/loa/requestContract": FileSignature,
  "/loa/bidStart": Gavel,
  "/loa/bidFinish": FlagTriangleRight,
  "/loa/contract": FileText,
  "/loa/requestChangeOrder": FilePlus2,
  "/loa/changeOrder": FileEdit,
  "/loa/payment": Receipt,
  "/loa/advancePayment": Wallet,
};

const AppSidebar = ({ bpName }) => {
  const navigate = useNavigate();
  const { isMobile, setOpenMobile } = useSidebar();
  const user = useRecoilValue(userState) ?? {};
  const logout = useLogout();

  const handleMenuClick = (path) => {
    navigate(path);
    if (isMobile) setOpenMobile(false);
  };

  const handleLogout = () => {
    Swal.fire({
      icon: "info",
      title: "로그아웃 하시겠습니까?",
      showCancelButton: true,
      confirmButtonText: "로그아웃",
      cancelButtonText: "취소",
    }).then((result) => {
      if (result.isConfirmed) logout();
    });
  };

  const initial = (user.name || user.username || "U").toString().charAt(0);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="px-4 py-3 border-b border-sidebar-border">
        <div className="flex items-center gap-2 group-data-[collapsible=icon]:justify-center">
          <div className="w-[26px] h-[26px] rounded-md bg-[#0080db] flex items-center justify-center text-[11px] font-bold text-white shrink-0">
            SHI
          </div>
          <span className="flex-1 font-semibold text-sidebar-foreground group-data-[collapsible=icon]:hidden">
            LOA 결재 시스템
          </span>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <Collapsible defaultOpen className="group/collapsible">
          <SidebarGroup>
            <SidebarGroupLabel asChild>
              <CollapsibleTrigger className="flex items-center gap-2">
                <span className="group-data-[collapsible=icon]:hidden flex-1 text-left">
                  품의함
                </span>
                <ChevronRight
                  size={14}
                  className="shrink-0 transition-transform group-data-[collapsible=icon]:hidden group-data-[state=open]/collapsible:rotate-90"
                />
              </CollapsibleTrigger>
            </SidebarGroupLabel>
            <CollapsibleContent>
              <SidebarMenu>
                {approvalMenuList.map((item) => {
                  const Icon = menuIconMap[item.path];
                  return (
                    <SidebarMenuItem key={item.path}>
                      <SidebarMenuButton
                        isActive={bpName === item.bpName}
                        tooltip={item.label}
                        onClick={() => handleMenuClick(item.path)}
                      >
                        <Icon size={16} />
                        <span>{item.label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
      </SidebarContent>
      <SidebarFooter className="border-t border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton className="flex items-center gap-2 h-auto py-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
                  <div className="w-7 h-7 rounded-full bg-[#eff6ff] flex items-center justify-center text-[#1d4ed8] text-xs font-bold shrink-0">
                    {initial}
                  </div>
                  <div className="flex-1 min-w-0 text-left group-data-[collapsible=icon]:hidden">
                    <p className="text-sm font-semibold text-sidebar-foreground truncate">
                      {user.name || user.username || "-"}
                    </p>
                    {user.name && user.username && (
                      <p className="text-xs text-muted-foreground truncate">
                        {user.username}
                      </p>
                    )}
                  </div>
                  <ChevronsUpDown
                    size={14}
                    className="shrink-0 text-muted-foreground group-data-[collapsible=icon]:hidden"
                  />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" align="start" className="w-56">
                <div className="px-3 py-2 space-y-1">
                  <p className="text-sm font-semibold text-foreground">
                    {user.name || user.username || "-"}
                  </p>
                  {user.name && user.username && (
                    <p className="text-xs text-muted-foreground truncate">
                      {user.username}
                    </p>
                  )}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="gap-2 cursor-pointer text-destructive">
                  로그아웃
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
};

export default AppSidebar;
